import { Box, Button, IconButton, InputAdornment, Stack, styled, Typography } from '@mui/material';
import { StyledParams } from 'types/common';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import React, { ChangeEvent, useMemo } from 'react';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { useInjection } from './hook';
import { SearchType } from 'types/loctions';
import { TableCustom } from 'app/components/elements/TableCustom';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { ReportFile } from 'types/report';
import { HEADER_REPORT_PAGE } from 'const/report';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { IOptions, ParentLocation } from 'types/room';
import EmailIcon from '@mui/icons-material/Email';
import { ModalSendMail } from './components/ModalSenMail';
import { lowerCase, startCase, uniqBy } from 'lodash';

const FILTER_CONTROL_HEIGHT = 46;

const alignedFilterFieldSx = {
  flexShrink: 0,
  '& .MuiFormHelperText-root': { display: 'none' },
  '& .MuiFormControl-root': { width: '100%', m: 0 },
  '& .MuiOutlinedInput-root': {
    minHeight: FILTER_CONTROL_HEIGHT,
    height: FILTER_CONTROL_HEIGHT,
    boxSizing: 'border-box',
    '& .MuiInputBase-input, & .MuiSelect-select': {
      padding: '10px 12px',
      minHeight: 'unset',
      height: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center'
    }
  }
};
export const getParentNameByLevel = (
  room: ParentLocation,
  level: number
): ParentLocation | undefined => {
  if (!room) return;

  if (room.level === level) {
    return room;
  }
  if (!room.parent) return undefined;
  return getParentNameByLevel(room.parent, level);
};

type RenderDataMapProps = {
  dataReportTable: ReportFile[];
  onSendMail?: (value: ReportFile) => void;
  onDelete?: (value: number) => void;
  onClickRoom: (value?: { key: string; value: string; label: string; level?: number }) => void;
  navigateIssuesPage: (url: string) => void;
  keySearch?: { key: string; value: string; label: string; level?: number }[];
  getLabelField: (type: string) => string;
  optionsLargestInTable: IOptions[];
};
const renderDataTableReport = ({
  dataReportTable,
  onClickRoom,
  keySearch,
  onSendMail,
  onDelete,
  getLabelField,
  optionsLargestInTable
}: RenderDataMapProps) => {
  // Consistent filter check
  const checkIsFilter = (key: string, level?: number) => {
    return !!keySearch?.find(
      (item) => item.key === key && (level === undefined || item.level === level)
    );
  };

  return dataReportTable.map((data) => {
    const room = data.room;
    const submitted = data.submitted;
    const reportIssuesOptions: Record<string, { value: JSX.Element }> = {};
    optionsLargestInTable.forEach((reportOption) => {
      reportIssuesOptions[`${getLabelField(reportOption?.type)}`] = {
        value: <></>
      };
    });
    (submitted?.options || []).forEach((reportOption) => {
      reportIssuesOptions[`${getLabelField(reportOption?.type)}`] = {
        value: <TextEllipsis text={reportOption?.value} width={'95%'} />
      };
    });

    const clientName = data?.room?.client?.clientName || data?.adHocIssue?.client?.clientName || '';
    const clientId =
      data?.room?.client?.id?.toString() || data?.adHocIssue?.client?.id?.toString() || '';

    return {
      documentControl: {
        value: (
          <TextEllipsis
            styles={{ cursor: 'pointer', fontWeight: 400 }}
            text={data?.title}
            width={'95%'}
          />
        )
      },
      clientName: {
        value: (
          <TextEllipsis
            onClick={() => {
              if (!clientId) return;
              onClickRoom({
                value: clientId,
                label: clientName,
                key: 'clientId'
              });
            }}
            text={clientName}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('clientId') ? 700 : 400,
              backgroundColor: checkIsFilter('clientId') ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      mainLocation: {
        value: (
          <TextEllipsis
            onClick={() => {
              const loc = getParentNameByLevel(room as ParentLocation, 1);
              if (!loc?.id) return;
              onClickRoom({
                value: loc.id?.toString() || '',
                label: loc.name || '',
                key: 'locationId',
                level: 1
              });
            }}
            text={
              getParentNameByLevel(room as ParentLocation, 1)?.name ||
              (data.adHocIssueId ? data.adHocIssue?.locationPathString || '' : '')
            }
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('locationId', 1) ? 700 : 400,
              backgroundColor: checkIsFilter('locationId', 1) ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      subLocation1: {
        value: (
          <TextEllipsis
            onClick={() => {
              const loc = getParentNameByLevel(room as ParentLocation, 2);
              if (!loc?.id) return;
              onClickRoom({
                value: loc.id?.toString() || '',
                label: loc.name || '',
                key: 'locationId',
                level: 2
              });
            }}
            text={getParentNameByLevel(room as ParentLocation, 2)?.name || ''}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('locationId', 2) ? 700 : 400,
              backgroundColor: checkIsFilter('locationId', 2) ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      subLocation2: {
        value: (
          <TextEllipsis
            onClick={() => {
              const loc = getParentNameByLevel(room as ParentLocation, 3);
              if (!loc?.id) return;
              onClickRoom({
                value: loc.id?.toString() || '',
                label: loc.name || '',
                key: 'locationId',
                level: 3
              });
            }}
            text={getParentNameByLevel(room as ParentLocation, 3)?.name || ''}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('locationId', 3) ? 700 : 400,
              backgroundColor: checkIsFilter('locationId', 3) ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      verticalLevel: {
        value: (
          <TextEllipsis
            onClick={() => {
              if (!data?.room?.parent?.id) return;
              onClickRoom({
                value: data?.room?.parent?.id?.toString(),
                label: data?.room?.parent?.name,
                key: 'locationId',
                level: 4
              });
            }}
            text={getParentNameByLevel(room as ParentLocation, 4)?.name || ''}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('locationId', 4) ? 700 : 400,
              backgroundColor: checkIsFilter('locationId', 4) ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      room: {
        value: (
          <TextEllipsis
            onClick={() => {
              if (!data?.room?.id) return;
              onClickRoom({
                value: data?.room?.id?.toString(),
                label: data?.room?.name,
                key: 'locationId',
                level: 5
              });
            }}
            text={getParentNameByLevel(room as ParentLocation, 5)?.name || ''}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('locationId', 5) ? 700 : 400,
              backgroundColor: checkIsFilter('locationId', 5) ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      complianceIssue: {
        value: (
          <TextEllipsis
            onClick={() => {
              submitted?.complianceRepresentative &&
                onClickRoom({
                  value: submitted.complianceRepresentative,
                  label: submitted.complianceRepresentative,
                  key: 'complianceRepresentative'
                });
            }}
            text={submitted?.complianceRepresentative || ''}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('complianceRepresentative') ? 700 : 400,
              backgroundColor: checkIsFilter('complianceRepresentative')
                ? 'rgba(0,0,0,0.2)'
                : 'unset'
            }}
          />
        )
      },
      createdAt: {
        value: (
          <TextEllipsis
            onClick={() => {
              data?.createdAt &&
                onClickRoom({
                  value: dayjs(data?.createdAt).format('YYYY-MM-DD'),
                  label: dayjs(data?.createdAt).format('YYYY-MM-DD'),
                  key: 'date'
                });
            }}
            text={dayjs(data?.createdAt).format('YYYY-MM-DD')}
            width={'95%'}
            styles={{
              fontWeight: checkIsFilter('date') ? 700 : 400,
              backgroundColor: checkIsFilter('date') ? 'rgba(0,0,0,0.2)' : 'unset'
            }}
          />
        )
      },
      ...reportIssuesOptions,
      action: {
        value: (
          <Stack alignItems={'flex-start'}>
            <Button style={{ gap: 10 }} onClick={() => onSendMail && onSendMail(data)}>
              Forward
              <EmailIcon />
            </Button>
            <Button
              onClick={() => {
                if (data?.storage?.url) {
                  window.open(data.storage.url, '_blank');
                }
              }}
              style={{ gap: 10 }}>
              View
              <img alt={'icon-eye'} src={'/icons/icon_eye.svg'} style={{ cursor: 'pointer' }} />
            </Button>
            <Button style={{ gap: 10 }} onClick={() => onDelete && onDelete(data.id)}>
              Delete
              <img
                alt={'icon-delete'}
                src={'/icons/icon_delete.svg'}
                style={{ cursor: 'pointer' }}
              />
            </Button>
          </Stack>
        )
      }
    };
  });
};
/**
 *
 * @constructor
 */
export const ReportPage = () => {
  const navigate = useNavigate();
  const {
    search,
    setSearch,
    searchType,
    setSearchType,
    pagingInfo,
    onSearch,
    sortOption,
    handleSort,
    dataReportTable,
    handleChangePage,
    setFilterValidations,
    filterValidations,
    dataSendMail,
    setDataSendMail,
    users,
    options,
    onUpdatePagination,
    asyncHandleSendMail,
    asyncHandleDeleteReport
  } = useInjection();

  const mapOptions = useMemo(() => {
    const mapOptions = new Map();
    options?.forEach((op) => {
      mapOptions.set(op.id.toString(), op.name);
    });
    return mapOptions;
  }, [options]);

  const tableHeaderReportFiles = useMemo(() => {
    const tableHeaders = [...HEADER_REPORT_PAGE];
    let reportOptions: IOptions[] = [];
    dataReportTable.forEach((item) => {
      const submittedOptions = item.submitted?.options ?? [];
      reportOptions = [...reportOptions, ...submittedOptions];
      reportOptions = uniqBy(reportOptions, 'type');
    });

    if (reportOptions?.length) {
      const insertIndex = tableHeaders.length - 1;
      const reportColumns = reportOptions.map((reportOption) => ({
        label: startCase(lowerCase(mapOptions.get(reportOption?.type) || reportOption?.type)),
        width: 200
      }));
      tableHeaders.splice(insertIndex, 0, ...reportColumns);
    }
    return { tableHeaders, reportOptions };
  }, [dataReportTable, mapOptions]);

  return (
    <WrapperCreatePage>
      <Stack
        width={'100%'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <TitlePageStyled>Report</TitlePageStyled>
      </Stack>
      <Stack
        flexDirection={'row'}
        width={'100%'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <Stack flexDirection={'row'} gap={1.25} alignItems={'center'} flexWrap={'wrap'}>
          <Box sx={{ ...alignedFilterFieldSx, width: 352 }}>
            <InputText
              startAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                  </IconButton>
                </InputAdornment>
              }
              placeholder={'Search'}
              inputProps={{
                value: search,
                onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setSearch(event.target.value);
                },
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (event.key === 'Enter') {
                    onSearch && onSearch();
                  }
                }
              }}
            />
          </Box>
          <ButtonCustom
            sx={{
              flexShrink: 0,
              width: 'max-content',
              height: `${FILTER_CONTROL_HEIGHT}px`,
              minHeight: `${FILTER_CONTROL_HEIGHT}px`
            }}
            label={'Search'}
            onClick={onSearch}
          />
          <Box sx={{ ...alignedFilterFieldSx, width: 180 }}>
            <InputSelect
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as SearchType)}
              options={[
                { label: 'Partial Text', value: 'partial-text' },
                { label: 'Full text', value: 'full-text' }
              ]}
              formControlStyle={{ width: '100%' }}
              sx={{ width: '100%', p: 0 }}
            />
          </Box>
          {/*<ButtonCustom*/}
          {/*  sx={{ width: 'max-content', height: '46px', ml: 2, backgroundColor: '#4E8A7A' }}*/}
          {/*  label={'View Background Jobs'}*/}
          {/*  onClick={() => navigate('/background-jobs')}*/}
          {/*/>*/}
        </Stack>
        {/*<Stack width={'352px'}>*/}
        {/*  <InputSelect*/}
        {/*    value={filter}*/}
        {/*    placeholder={'Filter by Asset Type'}*/}
        {/*    onChange={(e: SelectChangeEvent<unknown>) => {*/}
        {/*      setFilter(e.target.value as string);*/}
        {/*    }}*/}
        {/*    sx={{ minHeight: '46px', height: '46px', padding: 0 }}*/}
        {/*    options={[]}*/}
        {/*  />*/}
        {/*</Stack>*/}
      </Stack>
      <TableCustom
        isScrollX
        onSortHeader={handleSort}
        sort={sortOption}
        data={renderDataTableReport({
          keySearch: filterValidations,
          getLabelField: (type: string) => startCase(lowerCase(mapOptions.get(type) || type)),
          onClickRoom: (value?: { key: string; value: string; label: string; level?: number }) => {
            setFilterValidations(value);
            onUpdatePagination(1);
          },
          dataReportTable,
          navigateIssuesPage: (url) => navigate(url),
          onSendMail: (value) => {
            setDataSendMail(value);
          },
          onDelete: asyncHandleDeleteReport,
          optionsLargestInTable: tableHeaderReportFiles.reportOptions
        })}
        headers={tableHeaderReportFiles.tableHeaders}
        handleChooseRow={() => {}}
        styledTableRow={{ cursor: ' default ' }}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />
      <ModalSendMail
        isOpen={!!dataSendMail}
        onCloseModal={() => setDataSendMail(undefined)}
        options={
          users?.map((item) => ({
            value: item?.email || '',
            label: item.email || ''
          })) || []
        }
        onSubmit={asyncHandleSendMail}
      />
    </WrapperCreatePage>
  );
};
const WrapperCreatePage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: '20px'
}));
const TitlePageStyled = styled(Typography)(({ theme }: StyledParams) => ({
  margin: '20px 0',
  color: theme.colors.base,
  fontSize: '24px',
  fontWeight: 900,
  fontFamily: `'Avenir',sans-serif`
}));
